import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Package, 
  FlaskConical, 
  Factory, 
  FolderKanban,
  Wheat, 
  Truck as TruckDelivery, 
  BarChart3,
  LogOut,
  X,
  Settings,
  Pill,
  Truck,
  Calculator,
  Users
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { usePermissions } from '../../context/PermissionsContext';
import { Button } from '../ui/button';

const navItems = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard', permission: 'access_dashboard' },
  { to: '/inventory', icon: Package, label: 'Inventario' },
  { to: '/medications', icon: Pill, label: 'Medicamentos' },
  { to: '/products', icon: Wheat, label: 'Alimento' },
  { to: '/dispatches', icon: TruckDelivery, label: 'Despachos' },
  { to: '/projects', icon: FolderKanban, label: 'Proyectos' },
  { to: '/operational-costs', icon: Calculator, label: 'Costos Operativos' },
  { to: '/purchases', icon: Truck, label: 'Compras' },
  { to: '/reports', icon: BarChart3, label: 'Reportes' },
  { to: '/users', icon: Users, label: 'Usuarios', permission: 'access_users' },
  { to: '/settings', icon: Settings, label: 'Configuración', permission: 'access_settings' },
];

export function Sidebar({ isOpen, onClose }) {
  const { logout, user } = useAuth();
  const { permissions, role } = usePermissions();
  const navigate = useNavigate();

  // Filtrar items según permisos (admin ve todo)
  const visibleNavItems = navItems.filter(item => {
    if (!item.permission) return true;
    if (role === 'admin') return true;
    return !!permissions[item.permission];
  });

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={onClose}
          data-testid="sidebar-overlay"
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`
          fixed left-0 top-0 h-full w-64 bg-white border-r border-slate-200 z-50
          flex flex-col transition-transform duration-300
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0
        `}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="p-6 border-b border-slate-100">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-brand text-2xl font-bold text-[#931A25]">AVIPORK</h1>
              <p className="text-xs text-slate-500 mt-1">Crianza de Primera Calidad</p>
            </div>
            <Button 
              variant="ghost" 
              size="icon" 
              className="md:hidden"
              onClick={onClose}
              data-testid="close-sidebar-btn"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {visibleNavItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={onClose}
              className={({ isActive }) => `
                flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200
                ${isActive 
                  ? 'bg-[#931A25]/10 text-[#931A25] font-semibold' 
                  : 'text-slate-600 hover:bg-slate-100 hover:text-[#931A25]'
                }
              `}
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <item.icon className="h-5 w-5" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* User section */}
        <div className="p-4 border-t border-slate-100">
          <div className="flex items-center gap-3 px-4 py-2 mb-2">
            <div className="w-8 h-8 rounded-full bg-[#E97171] flex items-center justify-center text-white font-semibold">
              {user?.name?.charAt(0).toUpperCase() || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.name || 'Usuario'}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email || ''}</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            className="w-full justify-start text-slate-600 hover:text-red-600 hover:bg-red-50"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="h-4 w-4 mr-3" />
            Cerrar Sesión
          </Button>
        </div>
      </aside>
    </>
  );
}
